<template>
  <div class="country-list">
    <div
      v-if="$mq === 'desktop'"
      class="country-list__label"
    >
      <AmTypography
        wrapper-element="label"
        typography-style="l7"
        font-type="roboto"
        weight="regular"
        text-color="dark-80"
        :text="$t('containerLeft.footer.checkoutLocal')"
      />
    </div>
    <div
      v-if="selectedCountry"
      ref="input"
      tabindex="0"
      class="country-list__input"
      @click="toggleCountries"
      @keydown.down.prevent="prevListItem"
      @keydown.up.prevent="nextListItem"
      @keyup.enter="selectCountry(countries[currentCountry])"
    >
      <div class="country-list__selected">
        <AmTypography
          typography-style="l7"
          font-type="roboto"
          weight="regular"
          :text="getFlagIcon(selectedCountry.code)"
          class="country-list__flag"
        />
        <AmTypography
          v-if="$mq === 'desktop'"
          wrapper-element="span"
          typography-style="l7"
          font-type="roboto"
          weight="regular"
          text-color="dark-80"
          :text="selectedCountry.name"
        />
      </div>
      <AmIconMaterial
        name="expand_more"
        size="sm"
        color="#5C5C5C"
        class="country-list__arrow"
      />
    </div>
    <div
      v-show="showCountries"
      :class="{
        'country-list__list-wrapper--desktop': $mq === 'desktop',
        'country-list__list-wrapper--mobile': $mq !== 'desktop',
      }"
      @click="showCountries = false"
    >
      <div class="country-list__content">
        <div
          v-if="$mq !== 'desktop'"
          class="country-list__header"
        >
          <AmTypography
            class="country-list__title"
            wrapper-element="h1"
            typography-style="l7"
            font-type="roboto"
            weight="regular"
            text-color="dark-80"
            :text="$t('containerLeft.footer.checkoutLocal')"
          />
          <AmIconMaterial
            name="close"
            size="sm"
            color="#5C5C5C"
            class="country-list__close"
            @click="showCountries = false"
          />
        </div>
        <ul class="country-list__list">
          <li
            v-for="(country, index) in countries"
            :key="country.id"
            class="country-list__country"
            :class="{ 'country-list__country--selected': currentCountry === index }"
            @click="selectCountry(country)"
          >
            <AmTypography
              typography-style="l7"
              font-type="roboto"
              weight="regular"
              text-color="dark"
              :text="getFlagIcon(country.code)"
            />
            <AmTypography
              wrapper-element="span"
              typography-style="l6"
              font-type="roboto"
              weight="regular"
              text-color="dark"
              :text="country.name"
            />
            <AmTypography
              wrapper-element="span"
              typography-style="l6"
              font-type="roboto"
              weight="regular"
              text-color="dark"
              :text="`(${country.language})`"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
import { mapActions, mapGetters } from 'vuex'
import queryPersistance from '@/components/mixins/queryPersistance'

export default {
  name: 'CountryListSelect',
  mixins: [queryPersistance],
  data() {
    return {
      showCountries: false,
      selectedCountry: null,
      currentCountry: -1,
    }
  },
  computed: {
    ...mapGetters('Countries', {
      countries: 'getCountries',
    }),
    ...mapGetters('Cart', ['getCountryId']),
  },
  watch: {
    selectedCountry(value) {
      if (value.language === 'español' && this.$route.name === 'new.stepPayment') {
        this.$router.push({
          name: 'new.stepPayment-card',
          params: { history: true },
          query: { pl: this.getPl?.pl },
        })
      }
    },
  },
  async mounted() {
    document.addEventListener('click', this.closeCountries)

    await this.fetchCountries()
    this.selectedCountry = this.countries.find((country) => country.id === this.getCountryId)
    this.selectCurrentCountry(this.getCountryId)
    this.switchLanguage(this.selectedCountry.language)
    this.$refs.input?.addEventListener('blur', this.stayFocus)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeCountries)
    this.$refs.input?.removeEventListener('blur', this.stayFocus)
  },
  methods: {
    ...mapActions('Countries', ['fetchCountries', 'changeCountry']),
    stayFocus() {
      this.$refs.input.focus()
    },
    getFlagIcon(code) {
      return getUnicodeFlagIcon(code)
    },
    selectCountry(selectedCountry) {
      this.requestForChangeCountry(selectedCountry)
      this.showCountries = false
    },
    switchLanguage(language) {
      const mapperLanguageToI18nCode = {
        español: 'es',
        português: 'pt-BR',
      }
      this.$i18n.locale = mapperLanguageToI18nCode[language]
    },
    selectCurrentCountry(selectedCountryId) {
      this.currentCountry = this.countries.findIndex((country) => country.id === selectedCountryId)
    },
    async requestForChangeCountry(selectedCountry) {
      try {
        await this.changeCountry(selectedCountry.id)
        this.selectedCountry = selectedCountry
        this.selectCurrentCountry(selectedCountry.id)
        this.switchLanguage(selectedCountry.language)
      } catch (error) {
        this.$AmAlert({
          type: 'error',
          description: this.$t('countryListSelect.countrySelectError'),
          autoHide: true,
        })
      }
    },
    nextListItem() {
      if (this.currentCountry > 0) {
        this.currentCountry -= 1
        return
      }
      this.currentCountry = this.countries.length - 1
    },
    prevListItem() {
      if (this.countries.length - 1 > this.currentCountry) {
        this.currentCountry += 1
        return
      }
      this.currentCountry = 0
    },
    toggleCountries() {
      this.showCountries = !this.showCountries
    },
    closeCountries(e) {
      if (!this.$el.contains(e.target)) {
        this.showCountries = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.country-list {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 12px;
  position: relative;

  &__label {
    height: 24px;
  }

  &__content {
    border: 1px solid $light-40;
    width: 259px;
    background-color: $white;
    border-radius: 4px;
  }

  &__header {
    display: flex;
    padding: 15px 0px;
    margin: 0 16px;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    border-bottom: 1px solid $light-40;
  }

  &__close {
    cursor: pointer;
  }

  &__title {
    ::v-deep h1 {
      margin: 0;
    }
  }

  &__list {
    margin: 0;
    padding: 0;
    list-style-type: none;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    @include mobile {
      border-radius: 0;
      border: 0;
    }
  }

  &__list-wrapper--desktop {
    position: absolute;
    top: 0px;
    bottom: 0;
    right: 100px;
    top: 20px;
    left: -70px;
  }

  &__list-wrapper--mobile {
    position: fixed;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    overflow: auto;
    background-color: #{$dark-blue-120}66;
  }

  &__country {
    display: flex;
    padding: 12px 16px;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    cursor: pointer;

    &:hover,
    &--selected {
      background-color: $purple-20;
    }
  }

  &__input {
    display: flex;
    align-items: center;
    cursor: pointer;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: auto;
  }

  &__flag {
    align-self: center;
  }

  &__arrow {
    margin-left: 5px;
  }

  &__selected {
    display: flex;
    gap: 6px;
  }
}
</style>
